import React, { useState, useEffect } from 'react';

import { Row, Col, Spinner, Navbar, Nav } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';

import './GuidelinesPage.css';

import { useAppState } from '../AppContext';

import { enableUploads } from '../components/utils';

import SearchForm from '../components/SearchForm';
import { FaInfo, FaRegComment, FaUpload } from 'react-icons/fa';
import { GrHelp } from 'react-icons/gr';
import DocumentTermMatrix from '../components/matrix/DocumentTermMatrix';
import { getAvailableWidth } from '../components/utils';

import PdfPreviewModal from '../components/PdfPreviewModal';
import { OnboardingModal, desktopSlides } from '../components/OnboardingModal';
import { Redirect } from 'react-router-dom';

import FileUpload from '../components/FileUpload';

function MatrixPage() {
  const [pageNumber, setPageNumber] = useState(1);
  const [overlays, setOverlays] = useState([]);

  let [
    {
      documents,
      documentResults,
      searchString,
      searchInProgress,
      clusterData,
      documentCluster,
      pdfModalOpen,
      documentsInTable,
      onboardingModalOpen
    },
    dispatch
  ] = useAppState();

  const highlightSnippet = (snippet, document) => {
    dispatch({
      type: 'SET_SELECTED_SNIPPET',
      selectedSnippet: snippet ? snippet.id : null
    });
    setOverlays(snippet ? [snippet.misc] : []);

    dispatch({ type: 'SET_SELECTED_DOCUMENT', selectedDocument: document });

    const clusterID = documentCluster[document.id];
    const cluster = clusterData.filter((c) => c.id === clusterID)[0];

    console.log(cluster);
    if (!cluster.expanded) {
      dispatch({ type: 'TOGGLE_CLUSTER_COLLAPSED', cluster: cluster });
    }

    setPageNumber(snippet ? +snippet.misc.page : 1);
  };

  const searchFulltext = (newSearchString) => {
    dispatch({ type: 'SET_SELECTED_CLUSTER', selectedCluster: false });

    // if search string is empty, then clear search results
    if (!newSearchString) {
      dispatch({ type: 'SET_SEARCH_STRING', searchString: newSearchString });
      dispatch({ type: 'SET_DOCUMENT_RESULTS', documentResults: false });
      return;
    }

    const query = {
      method: 'solr',
      textQuery: newSearchString,
      includeSnippets: false
    };

    dispatch({ type: 'SET_SELECTED_DOCUMENT', selectedDocument: false });
    dispatch({ type: 'SET_SELECTED_SNIPPET', selectedSnippet: false });
    dispatch({ type: 'SET_SEARCH_STRING', searchString: newSearchString });

    dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: true });

    fetch('/nlp/documents/search', {
      method: 'POST', // or 'PUT'
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(query)
    })
      .then((response) => response.json())
      .then((data) => {
        console.log('Success:', data);
        const docs = data.documents;
        docs.sort((a, b) => b.score - a.score);
        dispatch({ type: 'SET_DOCUMENT_RESULTS', documentResults: docs });
        dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: false });
      })
      .catch((error) => {
        console.error('Error on search:', error);
        dispatch({ type: 'SET_SEARCH_IN_PROGRESS', searchInProgress: false });
      });
  };

  // fetch documents
  useEffect(() => {
    fetch('/api/documents')
      .then((res) => res.json())
      .then((data) => {
        if (data.documents && data.documents.length > 0) {
          dispatch({ type: 'SET_DOCUMENTS', documents: data.documents });
        }
      });
  }, [dispatch]);

  // fetch cluster data
  useEffect(() => {
    if (documents && !clusterData) {
      console.log('Getting cluster data');

      fetch('/nlp/documents/clusters', {
        method: 'GET', // or 'PUT'
        headers: {
          'Content-Type': 'application/json'
        }
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.clustergroup && data.clustergroup.clusters) {
            dispatch({
              type: 'SET_CLUSTER_DATA',
              clusterData: data.clustergroup.clusters
            });
          }
        });
    } else if (!documents) {
      console.log('Waiting for documents before getting cluster data');
    }
  }, [dispatch, documents]);

  if (!documents) {
    return (
      <Row className="limitedWidth">
        <Col>
          <h1>
            <Spinner
              as="span"
              animation="grow"
              role="status"
              aria-hidden="true"
            />
            Loading...
          </h1>

          <p>
            If page has not loaded after a few seconds, please email{' '}
            <a href="mailto:james.scott-brown@ed.ac.uk?subject='ClinicalGuidelines.net not loading'">
              james.scott-brown@ed.ac.uk
            </a>
            .
          </p>
        </Col>
      </Row>
    );
  }

  const HeaderBar = () => {
    const showUploadBox = getAvailableWidth() > 800 && enableUploads;

    return (
      <div
        style={{
          borderRadius: '10px',
          paddingLeft: '10px',
          paddingRight: '10px',

          position: 'relative',
          maxWidth: '100%',
          width: 'max-content',

          marginLeft: 'auto',
          marginRight: 'auto'
        }}
      >
        <Row
          style={{
            paddingLeft: '10px',
            lineHeight: '2.5rem',
            borderBottom: '1px solid #345f7d'
          }}
        >
          <Col sm={3}>
            <Row style={{ paddingTop: '1em' }}>
              <img
                src="/images/logo.png"
                style={{ maxWidth: '100%' }}
                className="d-inline-block align-top"
                alt="Project logo"
              />
            </Row>
          </Col>

          <Col sm={showUploadBox ? 5 : 9}>
            <Row>
              <Navbar
                style={{ paddingTop: '0px', paddingBottom: '0px' }}
                expand="lg"
                className={'navbar-right'}
              >
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
                <Navbar.Collapse
                  id="basic-navbar-nav"
                  className={'navbar-right'}
                >
                  <Nav variant="pills">
                    <Nav.Item>
                      <Nav.Link href="/about" id="about-link">
                        <FaInfo /> About
                      </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                      <Nav.Link href="/how-it-works" id="how-it-work-link">
                        <GrHelp /> How it works
                      </Nav.Link>
                    </Nav.Item>

                    <Nav.Item>
                      {enableUploads && (
                        <Nav.Link href="/upload" id="upload-link">
                          <FaUpload /> Upload
                        </Nav.Link>
                      )}
                    </Nav.Item>

                    {process.env.REACT_APP_FEEDBACK_LINK && (
                      <Nav.Item>
                        <Nav.Link
                          href={process.env.REACT_APP_FEEDBACK_LINK}
                          id="feedback-link"
                        >
                          <FaRegComment /> Give feedback
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </Nav>
                </Navbar.Collapse>
              </Navbar>
            </Row>

            <Row>
              <Col id="search-col">
                <div>
                  <div style={{ paddingLeft: '16px' }}>
                    <SearchForm
                      searchFulltext={searchFulltext}
                      searchString={searchString}
                    />
                  </div>
                  {/* <div style={{ width: '40%', float: 'right' }}>
                <FilterForm
                  setFilterString={(newFilterString) => dispatch({type: 'SET_FILTER_STRING', filterString: newFilterString})}
                  onInteraction={(ev) =>
                    dispatch({
                      type: 'SET_SELECTED_CLUSTER',
                      selectedCluster: false
                    })
                  }
                  filterString={filterString}
                />
              </div>
              */}
                </div>
              </Col>
            </Row>
          </Col>

          <Col id="upload-col" sm={4}>
            {showUploadBox && (
              <FileUpload
                onFileSelection={<Redirect to={'/upload'} />}
                showFileTypeMessage={false}
                containerStyle={{
                  top: '50%',
                  height: '100px',
                  marginTop: '-50px',
                  position: 'inherit'
                }}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  };

  const SearchingSpinner = () => (
    <div style={{ display: 'flex', height: '100%' }}>
      <h1 style={{ margin: 'auto' }}>
        Searching...
        <Spinner
          as="span"
          animation="border"
          role="status"
          aria-hidden="true"
        />
      </h1>
    </div>
  );

  const NoResults = () => (
    <div
      style={{
        display: 'flex',
        height: '100%',
        width: 'max-content',
        margin: 'auto'
      }}
    >
      <h1>No search results.</h1>
    </div>
  );

  return (
    <>
      {getAvailableWidth() > 750 ? <HeaderBar /> : <HeaderBar />}

      <Row>
        {pdfModalOpen && (
          <PdfPreviewModal setOverlays={setOverlays} overlays={overlays} />
        )}

        {searchInProgress ? (
          <SearchingSpinner />
        ) : documentResults !== false && documentResults.length === 0 ? (
          <NoResults />
        ) : (
          documents && (
            <DocumentTermMatrix
              documentsInTable={documentsInTable}
              highlightSnippet={highlightSnippet}
            />
          )
        )}

        {onboardingModalOpen && <OnboardingModal slides={desktopSlides} />}
      </Row>
    </>
  );
}

export default MatrixPage;
